import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

interface LiveAnnouncerContextType {
    /** Announce a message to screen readers via the global aria-live region. */
    announce: (message: string, priority?: 'polite' | 'assertive') => void;
}

const LiveAnnouncerContext = createContext<LiveAnnouncerContextType | undefined>(undefined);

/**
 * Provides a global aria-live region for screen reader announcements.
 * Wrap the app in this provider to enable any component to announce
 * dynamic changes (saves, errors, status updates) to screen readers.
 */
export const LiveAnnouncerProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [politeMessage, setPoliteMessage] = useState('');
    const [assertiveMessage, setAssertiveMessage] = useState('');
    const clearTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

    const announce = useCallback((message: string, priority: 'polite' | 'assertive' = 'polite') => {
        // Clear previous message first to ensure re-announcement of identical text
        if (priority === 'assertive') {
            setAssertiveMessage('');
        } else {
            setPoliteMessage('');
        }

        // Clear any pending timeout
        if (clearTimeoutRef.current) {
            clearTimeout(clearTimeoutRef.current);
        }

        // Set the message on next tick so the DOM change is detected by screen readers
        requestAnimationFrame(() => {
            if (priority === 'assertive') {
                setAssertiveMessage(message);
            } else {
                setPoliteMessage(message);
            }
        });

        // Auto-clear after 5 seconds to avoid stale announcements
        clearTimeoutRef.current = setTimeout(() => {
            setPoliteMessage('');
            setAssertiveMessage('');
        }, 5000);
    }, []);

    return (
        <LiveAnnouncerContext.Provider value={{ announce }}>
            {children}
            {/* WCAG aria-live regions — visually hidden, read by screen readers */}
            <div
                aria-live="polite"
                aria-atomic="true"
                role="status"
                className="sr-only"
            >
                {politeMessage}
            </div>
            <div
                aria-live="assertive"
                aria-atomic="true"
                role="alert"
                className="sr-only"
            >
                {assertiveMessage}
            </div>
        </LiveAnnouncerContext.Provider>
    );
};

/**
 * Hook to announce messages to screen readers.
 * Usage: const { announce } = useLiveAnnouncer();
 *        announce('Projekt gespeichert');
 *        announce('Fehler beim Speichern', 'assertive');
 */
export const useLiveAnnouncer = (): LiveAnnouncerContextType => {
    const context = useContext(LiveAnnouncerContext);
    if (!context) {
        throw new Error('useLiveAnnouncer must be used within a LiveAnnouncerProvider');
    }
    return context;
};
